// Deutsche Registergerichte (Amtsgerichte, die das Handelsregister führen),
// gruppiert nach Bundesland. Quelle: „Liste deutscher Registergerichte"
// (handelsregister.de / Landesjustiz). Stand 2026-07; bei Änderungen der
// Gerichtsorganisation hier pflegen.
//
// Verwendung: Dropdown „Zuständiges Amtsgericht" bei der Firmen-Anlage und in
// den Kontaktdetails. Gespeichert wird der reine Ortsname (z. B. „München");
// die Anzeige stellt „Amtsgericht " voran → „Amtsgericht München HRB 12345".

export type AmtsgerichtGruppe = { bundesland: string; gerichte: string[] };

export const AMTSGERICHTE_BY_BUNDESLAND: AmtsgerichtGruppe[] = [
  { bundesland: "Baden-Württemberg", gerichte: ["Freiburg", "Mannheim", "Stuttgart", "Ulm"] },
  {
    bundesland: "Bayern",
    gerichte: [
      "Amberg", "Ansbach", "Aschaffenburg", "Augsburg", "Bamberg", "Bayreuth", "Coburg",
      "Deggendorf", "Fürth", "Hof", "Ingolstadt", "Kempten (Allgäu)", "Landshut", "Memmingen",
      "München", "Nürnberg", "Passau", "Regensburg", "Schweinfurt", "Straubing", "Traunstein",
      "Weiden i.d.OPf.", "Würzburg",
    ],
  },
  { bundesland: "Berlin", gerichte: ["Berlin-Charlottenburg"] },
  { bundesland: "Brandenburg", gerichte: ["Cottbus", "Frankfurt (Oder)", "Neuruppin", "Potsdam"] },
  { bundesland: "Bremen", gerichte: ["Bremen"] },
  { bundesland: "Hamburg", gerichte: ["Hamburg"] },
  {
    bundesland: "Hessen",
    gerichte: [
      "Bad Hersfeld", "Bad Homburg v.d.H.", "Darmstadt", "Eschwege", "Frankfurt am Main",
      "Friedberg", "Fritzlar", "Fulda", "Gießen", "Hanau", "Kassel", "Königstein i.T.",
      "Korbach", "Limburg", "Marburg", "Offenbach am Main", "Wetzlar", "Wiesbaden",
    ],
  },
  { bundesland: "Mecklenburg-Vorpommern", gerichte: ["Neubrandenburg", "Rostock", "Schwerin", "Stralsund"] },
  {
    bundesland: "Niedersachsen",
    gerichte: [
      "Aurich", "Braunschweig", "Göttingen", "Hannover", "Hildesheim", "Lüneburg",
      "Oldenburg (Oldenburg)", "Osnabrück", "Stadthagen", "Tostedt", "Walsrode",
    ],
  },
  {
    bundesland: "Nordrhein-Westfalen",
    gerichte: [
      "Aachen", "Arnsberg", "Bad Oeynhausen", "Bielefeld", "Bochum", "Bonn", "Coesfeld",
      "Dortmund", "Duisburg", "Düren", "Düsseldorf", "Essen", "Gelsenkirchen", "Gütersloh",
      "Hagen", "Hamm", "Iserlohn", "Kleve", "Köln", "Krefeld", "Lemgo", "Mönchengladbach",
      "Münster", "Neuss", "Paderborn", "Recklinghausen", "Siegburg", "Siegen", "Steinfurt",
      "Wuppertal",
    ],
  },
  {
    bundesland: "Rheinland-Pfalz",
    gerichte: [
      "Bad Kreuznach", "Kaiserslautern", "Koblenz", "Landau i.d.Pfalz", "Ludwigshafen am Rhein",
      "Mainz", "Montabaur", "Wittlich", "Zweibrücken",
    ],
  },
  { bundesland: "Saarland", gerichte: ["Saarbrücken"] },
  { bundesland: "Sachsen", gerichte: ["Chemnitz", "Dresden", "Leipzig"] },
  { bundesland: "Sachsen-Anhalt", gerichte: ["Stendal"] },
  { bundesland: "Schleswig-Holstein", gerichte: ["Flensburg", "Kiel", "Lübeck", "Pinneberg"] },
  { bundesland: "Thüringen", gerichte: ["Jena"] },
];

/** Flache, alphabetisch sortierte Liste aller Registergerichts-Orte. */
export const AMTSGERICHTE: string[] = AMTSGERICHTE_BY_BUNDESLAND
  .flatMap((g) => g.gerichte)
  .sort((a, b) => a.localeCompare(b, "de"));

/**
 * Options-Liste für die suchbare Combobox (SearchableSelect): Ort als
 * value/label, Bundesland als rechts-bündiger `hint`. Alphabetisch sortiert.
 */
export const AMTSGERICHT_OPTIONS: { value: string; label: string; hint: string }[] =
  AMTSGERICHTE_BY_BUNDESLAND
    .flatMap((g) => g.gerichte.map((ort) => ({ value: ort, label: ort, hint: g.bundesland })))
    .sort((a, b) => a.label.localeCompare(b.label, "de"));

/**
 * Anzeige-String für das Handelsregister in den Kontaktdetails:
 *   „Amtsgericht München HRB 12345".
 * Robust gegen Alt-Daten: enthält die Nummer keinen Registerart-Präfix
 * (Buchstaben), wird der separat gespeicherte `register`-Typ (HRA/HRB/…)
 * vorangestellt. Fehlt das Amtsgericht, wird nur die Nummer gezeigt.
 */
export function formatHandelsregister(opts: {
  registergericht?: string | null;
  handelsregisterNummer?: string | null;
  register?: string | null;
}): string {
  const gericht = (opts.registergericht ?? "").trim();
  const reg = (opts.register ?? "").trim();
  let nr = (opts.handelsregisterNummer ?? "").trim();
  // Alt-Daten: reine Nummer ohne Buchstaben + separater Registertyp → Typ voranstellen.
  if (nr && reg && !/[A-Za-z]/.test(nr)) nr = `${reg} ${nr}`;
  if (!gericht && !nr) return "";
  if (!gericht) return nr;
  if (!nr) return `Amtsgericht ${gericht}`;
  return `Amtsgericht ${gericht} ${nr}`;
}
